import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { type AgGrid, Button, Flex, Grid, ModalV2, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { modalEditLink, modalSubtitle } from "./styles_vanilla.css";
import { type TaxOverviewVariant, type TaxPerson } from "./utils";

const TITLE: Record<TaxOverviewVariant, string> = {
  fatca: "FATCA Substantial owner",
  crs: "CRS controlling persons",
};

const EDIT_LABEL: Record<TaxOverviewVariant, string> = {
  fatca: "Edit FATCA substantial owners",
  crs: "Edit CRS controlling persons",
};

const nameColumn: AgGrid.ColDef<TaxPerson> = {
  field: "name",
  headerName: "Name",
  minWidth: 180,
  flex: 1.2,
};

const formatNullable = ({ value }: { value?: string | null }): string => value ?? "--";

const residenceAddressColumn: AgGrid.ColDef<TaxPerson> = {
  field: "residenceAddress",
  headerName: "Residence address",
  minWidth: 220,
  flex: 1.5,
  autoHeight: true,
  wrapText: true,
  valueFormatter: formatNullable,
};

const tinColumn: AgGrid.ColDef<TaxPerson> = {
  field: "tin",
  headerName: "TIN",
  minWidth: 140,
  flex: 1,
  valueFormatter: formatNullable,
};

const crsTypeColumn: AgGrid.ColDef<TaxPerson> = {
  field: "controllingPersonType",
  headerName: "CRS controlling person type",
  minWidth: 200,
  flex: 1.2,
  valueFormatter: formatNullable,
};

const COLUMNS_BY_VARIANT: Record<TaxOverviewVariant, AgGrid.ColDef<TaxPerson>[]> = {
  fatca: [nameColumn, residenceAddressColumn, tinColumn],
  crs: [nameColumn, crsTypeColumn, residenceAddressColumn, tinColumn],
};

const GRID_OPTIONS = {
  rowHeight: 64,
  headerHeight: 48,
  autoSizeStrategy: { type: "fitGridWidth" } as const,
  domLayout: "autoHeight" as const,
  suppressCellFocus: true,
};

export type TaxPersonsModalProps = {
  show: boolean;
  variant: TaxOverviewVariant;
  subtitle: string;
  persons: TaxPerson[];
  editUrl: string | null;
  onClose: () => void;
  readonly?: boolean;
};

export const TaxPersonsModal = ({
  show,
  variant,
  subtitle,
  persons,
  editUrl,
  onClose,
  readonly = false,
}: TaxPersonsModalProps) => {
  const history = useHistory();
  const columnDefs = useMemo(() => COLUMNS_BY_VARIANT[variant], [variant]);

  const handleEditClick = useCallback(() => {
    if (!editUrl) return;
    onClose();
    history.push(editUrl);
  }, [editUrl, onClose, history]);

  return (
    <ModalV2 show={show} onHide={onClose} title={TITLE[variant]} size="large">
      <ModalV2.Body>
        <Text variant="secondary-md" className={modalSubtitle}>
          {subtitle}
        </Text>
        <Grid<TaxPerson>
          columnDefs={columnDefs}
          rowData={persons}
          gridOptions={GRID_OPTIONS}
          reactiveCustomComponents
        />
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex
          justifyContent={editUrl && !readonly ? "space-between" : "flex-end"}
          alignItems="center"
          gap={Sizes.MD}
        >
          {editUrl && !readonly && (
            <Button className={modalEditLink} displayAsLink onClick={handleEditClick}>
              {EDIT_LABEL[variant]}
            </Button>
          )}
          <Button variant="neutral" onClick={onClose}>
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
